import humps from 'humps'
import api from '@/api'
import getCurrentUserId from '@/utils/get-current-user-id'

export const KIND_MARKET = 'market'
export const KIND_LIFE_INSURANCE = 'lifeinsurance'
export const KIND_CAPITALISATION = 'capitalisation'
export const KIND_PEA = 'pea'
export const KIND_SAVINGS = 'savings'
export const KIND_PEE = 'pee'
export const KIND_PERCO = 'perco'
export const KIND_ARTICLE83 = 'article83'
export const KIND_RSP = 'rsp'
export const KIND_CHECKING = 'checking'
export const KIND_CARD = 'card'
export const KIND_DEPOSIT = 'deposit'
export const SAVING_KIND_LIVRET_A = 'livret_a'
export const SAVING_KIND_LIVRET_JEUNE = 'livret_jeune'
export const SAVING_KIND_LDD = 'ldd'
export const SAVING_KIND_PEL = 'pel'
export const SAVING_KIND_CEL = 'cel'
export const SAVING_KIND_LEP = 'lep'
export const KIND_LOAN = 'loan'

export const kindRepartitionWhitelist = [
  KIND_MARKET,
  KIND_LIFE_INSURANCE,
  KIND_CAPITALISATION,
  KIND_PEA,
  KIND_SAVINGS,
  KIND_PEE,
  KIND_PERCO,
  KIND_ARTICLE83,
  KIND_RSP,
  KIND_CHECKING,
  KIND_CARD,
  KIND_DEPOSIT
]

export const bankContractKinds = [
  KIND_CHECKING,
  KIND_CARD,
  KIND_DEPOSIT
]

export const supportedContractKinds = [
  KIND_LIFE_INSURANCE,
  KIND_CAPITALISATION,
  KIND_PEA,
  KIND_PEE,
  KIND_MARKET,
  KIND_SAVINGS,
  KIND_CHECKING,
  KIND_CARD,
  KIND_DEPOSIT,
  KIND_LOAN
]

export const riskedContractKinds = [
  KIND_LIFE_INSURANCE,
  KIND_CAPITALISATION,
  KIND_PEA,
  KIND_MARKET,
  KIND_PERCO,
  KIND_PEE,
  KIND_ARTICLE83
]

export const supportedSavingsKinds = [
  SAVING_KIND_LIVRET_A,
  SAVING_KIND_LIVRET_JEUNE,
  SAVING_KIND_LDD,
  SAVING_KIND_PEL,
  SAVING_KIND_CEL,
  SAVING_KIND_LEP
]

export const investmentKinds = [
  'saving_precautionary',
  'capital_increase',
  'pension',
  'real_estate',
  'transmission',
  'future_children',
  'other'
]

export const feesList = [
  'entryFee',
  'exitFee',
  'depositFee',
  'euroFundsFees',
  'ucsFee',
  'managementFee'
]

export const feesSpecialList = [
  'entryFee',
  'exitFee',
  'depositFee',
  'ucsFee',
  'managementFee',
  'transferFee'
]

export function isLifeInsurance ({ kind }) {
  return kind === KIND_LIFE_INSURANCE || kind === KIND_CAPITALISATION
}

export function isMarket ({ kind }) {
  return kind === KIND_MARKET
}

export function isPEA ({ kind }) {
  return kind === KIND_PEA
}

export function isRisked ({ kind }) {
  return riskedContractKinds.includes(kind)
}

export function isSupportedKind ({ kind }) {
  return supportedContractKinds.includes(kind)
}

export function isSupportedSaving ({ kindDetail }) {
  return supportedSavingsKinds.includes(kindDetail)
}

export function isSupported (contract) {
  return isSupportedKind(contract) && (contract.kind !== KIND_SAVINGS || isSupportedSaving(contract))
}

export function getHistoryTime ({ history }) {
  return {
    from: history ? new Date(history[0][0]) : Date.now(),
    to: history ? new Date(history[history.length - 1][0]) : Date.now()
  }
}

export function isBankAccount ({ kind }) {
  return bankContractKinds.includes(kind)
}

export default {
  fetch () {
    return api.get(`/users/${getCurrentUserId()}/portfolios`)
  },

  fetchLoans () {
    return api.get(`/users/${getCurrentUserId()}/loans`)
  },

  fetchById (id) {
    return api.get(`/users/${getCurrentUserId()}/portfolios/${id}`)
  },

  fetchHistory (id, from) {
    return api.get(`/users/${getCurrentUserId()}/portfolios/${id}/series/cash_values${from ? ('?from=' + from) : ''}`)
  },

  fetchAnalyses (portfolioId, section = '') {
    return api.get(`/users/${getCurrentUserId()}/portfolios/${portfolioId}/analyses/${section}`)
  },

  fetchByAccount (accountId) {
    return api.get(`/users/${getCurrentUserId()}/accounts/${accountId}/portfolios`)
  },

  fetchCompleteHistory (subset = 'all') {
    return api.get(`/users/${getCurrentUserId()}/portfolios/series/cash_values`, { params: humps.decamelizeKeys({ portfolioSubset: subset }) })
  },

  fetchOptimize (portfolioId, params) {
    return api.post(`/users/${getCurrentUserId()}/portfolios/${portfolioId}/optimize`, { data: params })
  },

  fetchQuestions () {
    return api.get(`/risk_survey`)
  },

  fetchTargets () {
    return api.get(`/portfolios/targets`)
  },

  fetchRepartition () {
    return api.get(`/users/${getCurrentUserId()}/portfolios/composition?type=kind`)
  },

  fetchAggregateContractRepartition () {
    return api.post(`/users/${getCurrentUserId()}/portfolios/aggregate`,
      {
        'filters': [
          {
            'field': 'category',
            'match_any': ['invested_wealth', 'savings']
          }
        ],
        'aggregates': [
          {
            'id': 'accountsRepartition',
            'reducer': {
              'func': 'sum',
              'params': ['amount']
            },
            'segments': [
              {
                'field': 'kind'
              }
            ]
          }
        ]
      }
    )
  },

  fetchAggregateRealestateType () {
    return api.post(`/users/${getCurrentUserId()}/portfolios/aggregate`,
      {
        'filters': [
          {
            'field': 'category',
            'match_any': ['real_estate']
          }
        ],
        'aggregates': [
          {
            'id': 'realestateType',
            'reducer': {
              'func': 'sum',
              'params': ['amount']
            },
            'segments': [
              {
                'field': 'type'
              }
            ]
          }
        ]
      }
    )
  },

  fetchHistoryAll () {
    return api.get(`/users/${getCurrentUserId()}/portfolios/series/cash_values`,
      {
        params: {
          portfolio_subset: 'unscoped',
          categories: ['savings', 'invested_wealth', 'real_estate']
        }
      }
    )
  },

  fetchHistoryWealth () {
    return api.get(`/users/${getCurrentUserId()}/portfolios/series/cash_values`,
      {
        params: {
          portfolio_subset: 'unscoped',
          categories: ['savings', 'invested_wealth']
        }
      }
    )
  },

  fetchAggregateRealestateEnv () {
    return api.post(`/users/${getCurrentUserId()}/portfolios/aggregate`,
      {
        'filters': [
          {
            'field': 'category',
            'match_any': ['invested_wealth', 'savings', 'real_estate']
          }
        ],
        'aggregates': [
          {
            'id': 'realestateEnv',
            'stats': true,
            'reducer': {
              'func': 'sum',
              'params': ['amount']
            },
            'segments': [
              {
                'field': 'category',
                'clusterer': {
                  'type': 'mapper',
                  'mappings': [
                    { 'map': 'savings', 'to': 'wealth' },
                    { 'map': 'invested_wealth', 'to': 'wealth' },
                    { 'map': 'real_estate', 'to': 'real_estate' }
                  ]
                }
              }
            ]
          }
        ]
      }
    )
  },

  fetchComposition (subset, type, limit = null) {
    return api.get(`/users/${getCurrentUserId()}/portfolios/composition`, { params: humps.decamelizeKeys({ portfolioSubset: subset, type, limit }) })
  },

  fetchTransactions (portfolioId, page = 1, filters) {
    return api.get(`/users/${getCurrentUserId()}/portfolios/${portfolioId}/transactions`, { params: humps.decamelizeKeys({ ...filters, page }) })
  },

  fetchTransactionCategories () {
    return api.get(`/transactions/categories`)
  },

  fetchTransactionsMovements (id, from) {
    return api.get(`/users/${getCurrentUserId()}/portfolios/${id}/series/transactions_movements${from ? ('?from=' + from) : ''}`)
  },

  update (portfolioId, data) {
    return api.put(`/portfolios/${portfolioId}`, {portfolio: data})
  },

  updateInfos (portfolioId, infos) {
    return api.put(`/portfolios/${portfolioId}/portfolio_infos`, {portfolio_infos: infos})
  },

  updateStatus (portfolioId, status) {
    return api.put(`/portfolios/${portfolioId}`, {status: status})
  },
  fetchCardsList (filters = {}) {
    return api.get(`/contracts`, { params: humps.decamelizeKeys(filters) })
  },
  fetchCard (slug) {
    return api.get(`/contracts/${slug}`)
  },
  createCard (bankId, data) {
    return api.post(`/banks/${bankId}/contracts`, data)
  },
  updateCard (slug, data) {
    return api.put(`/contracts/${slug}`, data)
  },

  fetchLoansHistory (subset, from) {
    return api.get(`/users/${getCurrentUserId()}/portfolios/series/cash_values`, { params: humps.decamelizeKeys({ portfolioSubset: subset, from }) })
  },

  linkLoanToRealEstate (loanId, realEstateId) {
    return api.put(`/portfolios/${loanId}`, {portfolio: {realEstateId}})
  }
}
